/**
 * Masterclass Topic Library Data Module (Frontend)
 * Standardized creative industry curriculum topics for Synergy Multimedia Academy.
 */

export const MASTERCLASS_TOPIC_CATEGORIES = [
  'All Categories',
  'Photography & Visual Storytelling',
  'Videography & Film Production',
  'Graphic Design & Visual Communication',
  'Brand Strategy & Creative Direction',
  'AI Tools & Future Creative Skills',
  'Creative Business, Freelancing & Agency Growth',
  'Career Development & Personal Branding',
]

export const MASTERCLASS_TOPIC_LIBRARY = [
  // ── 1. Photography & Visual Storytelling ────────────────────────────────────
  {
    id: 'photo-01',
    title: 'How I Built My Photography Business',
    category: 'Photography & Visual Storytelling',
    duration_minutes: 120,
    description: 'A comprehensive roadmap for transitioning from hobbyist shooter to commercial photographer. Covers pricing packages, client contracts, client retention, and equipment investments.',
    suggested_takeaways: [
      { label: 'Client Retention', description: 'Strategies to convert one-off photography gigs into recurring corporate retainer contracts.' },
      { label: 'Equipment Strategy', description: 'Investing in high-ROI gear and maximizing studio and natural lighting capabilities.' },
      { label: 'Pricing Structures', description: 'Structuring day rates, licensing fees, and post-production deliverables.' },
    ],
  },
  {
    id: 'photo-02',
    title: 'Mastering Natural and Studio Lighting',
    category: 'Photography & Visual Storytelling',
    duration_minutes: 120,
    description: 'Learn the physics of light, directional modifiers, strobe control, and ambient mixing to achieve cinematic portraits in any environment.',
    suggested_takeaways: [
      { label: 'Modifier Control', description: 'Using softboxes, reflectors, and scrims to shape specular highlights and shadows.' },
      { label: 'Mixed Lighting', description: 'Balancing artificial strobe color temperature with ambient golden hour light.' },
      { label: 'Studio Workflow', description: 'Key, fill, and rim light positioning for editorial portraits.' },
    ],
  },
  {
    id: 'photo-03',
    title: 'Commercial Portraiture and Editorial Posing',
    category: 'Photography & Visual Storytelling',
    duration_minutes: 120,
    description: 'Guiding non-professional models and corporate executives to pose naturally while communicating brand authority and warmth.',
    suggested_takeaways: [
      { label: 'Posing Direction', description: 'Clear verbal cues and micro-adjustments for body posture and facial expression.' },
      { label: 'Framing & Composition', description: 'Golden ratio and negative space framing for commercial magazine layouts.' },
    ],
  },
  {
    id: 'photo-04',
    title: 'High-End Color Grading in Capture One & Lightroom',
    category: 'Photography & Visual Storytelling',
    duration_minutes: 120,
    description: 'Professional skin tone grading, tonal curves, color balance calibration, and creating bespoke style profiles for commercial campaigns.',
    suggested_takeaways: [
      { label: 'Skin Tone Accuracy', description: 'Isolating skin tones with selective color vectors and curve adjustments.' },
      { label: 'Profile Creation', description: 'Building signature color presets that maintain color fidelity across varied shoots.' },
    ],
  },
  {
    id: 'photo-05',
    title: 'Documentary Storytelling and Photojournalism Ethics',
    category: 'Photography & Visual Storytelling',
    duration_minutes: 120,
    description: 'Capturing unscripted human stories with empathy, ethical awareness, and narrative depth for NGOs, publications, and archival records.',
    suggested_takeaways: [
      { label: 'Story Arc', description: 'Structuring a photographic essay with establishing, detail, and climax moments.' },
      { label: 'Ethical Consent', description: 'Respecting subjects and navigating consent in visual documentation.' },
    ],
  },
  {
    id: 'photo-06',
    title: 'Studio Lighting for High Fashion and Beauty',
    category: 'Photography & Visual Storytelling',
    duration_minutes: 120,
    description: 'Advanced beauty dishes, grid spots, specular edge rim lights, and precision highlight control for high-fashion editorial imagery.',
    suggested_takeaways: [
      { label: 'Beauty Dish Technique', description: 'Positioning high-contrast beauty light for clean skin definition.' },
      { label: 'Catchlight Precision', description: 'Crafting circular and octagonal specular catchlights in subject eyes.' },
    ],
  },
  {
    id: 'photo-07',
    title: 'Architectural & Real Estate Photography',
    category: 'Photography & Visual Storytelling',
    duration_minutes: 120,
    description: 'Perspective correction, tilt-shift lens techniques, HDR interior balancing, and window pull exposures for architectural portfolios.',
    suggested_takeaways: [
      { label: 'Vertical Line Alignment', description: 'Eliminating converging vertical distortion in interior rooms.' },
      { label: 'Flambient Blending', description: 'Merging flash and ambient exposures for true-to-life interior colors.' },
    ],
  },
  {
    id: 'photo-08',
    title: 'Product & E-Commerce Photography That Converts',
    category: 'Photography & Visual Storytelling',
    duration_minutes: 120,
    description: 'Tabletop lighting, seamless white infinity sweeps, acrylic reflections, and focus-stacking for luxury commercial products.',
    suggested_takeaways: [
      { label: 'Highlight Diffusion', description: 'Managing reflections on glass, jewelry, and metallic textures.' },
      { label: 'Focus Stacking', description: 'Merging multi-plane focal exposures for edge-to-edge sharpness.' },
    ],
  },
  {
    id: 'photo-09',
    title: 'Street Photography & Visual Candidness',
    category: 'Photography & Visual Storytelling',
    duration_minutes: 120,
    description: 'Zone focusing, blending into urban surroundings, anticipating decisive moments, and visual geometry in street photography.',
    suggested_takeaways: [
      { label: 'The Decisive Moment', description: 'Anticipating subject motion and geometry before pressing the shutter.' },
      { label: 'Zone Focus Method', description: 'Pre-setting focal depth for split-second street captures.' },
    ],
  },
  {
    id: 'photo-10',
    title: 'Night Photography & Long Exposure Magic',
    category: 'Photography & Visual Storytelling',
    duration_minutes: 120,
    description: 'Astrophotography basics, light painting, traffic light trails, ND filters, and managing digital sensor noise during long exposures.',
    suggested_takeaways: [
      { label: 'Exposure Calculation', description: 'Calculating reciprocity and bulb exposure timing for night landscapes.' },
      { label: 'Noise Management', description: 'Dark frame subtraction and ISO noise floor optimization.' },
    ],
  },
  {
    id: 'photo-11',
    title: 'Post-Production Retouching & Frequency Separation',
    category: 'Photography & Visual Storytelling',
    duration_minutes: 120,
    description: 'Non-destructive Photoshop workflows: 16-bit frequency separation, micro dodge and burn, color matching, and preserving natural skin texture.',
    suggested_takeaways: [
      { label: 'Frequency Separation', description: 'Separating high-frequency texture from low-frequency color tones.' },
      { label: 'Dodge and Burn', description: 'Sculpting dimensional facial highlights with micro luminosity masks.' },
    ],
  },

  // ── 2. Videography & Film Production ───────────────────────────────────────
  {
    id: 'video-01',
    title: 'Behind the Scenes of Commercial Production',
    category: 'Videography & Film Production',
    duration_minutes: 120,
    description: 'Deconstructing real-world television commercials and corporate brand films from client brief to production sets and post-production delivery.',
    suggested_takeaways: [
      { label: 'Pre-Production Planning', description: 'Storyboards, shot lists, location scouting, and call sheets.' },
      { label: 'On-Set Directing', description: 'Managing crew, talent, sound recordists, and director of photography synchronization.' },
      { label: 'Delivery Pipelines', description: 'Master export specs for broadcast, social platforms, and digital billboards.' },
    ],
  },
  {
    id: 'video-02',
    title: 'Cinematography Fundamentals: Composition, Movement & Lenses',
    category: 'Videography & Film Production',
    duration_minutes: 120,
    description: 'Explore visual rhythm, camera movement motivation, focal length perspective, and dynamic shutter control to elevate visual storytelling.',
    suggested_takeaways: [
      { label: 'Camera Movement', description: 'When to use handheld grit vs. stabilized gimbal or slider tracks.' },
      { label: 'Lens Selection', description: 'Perspective compression with telephoto lenses versus wide-angle immersive context.' },
    ],
  },
  {
    id: 'video-03',
    title: 'Professional Location Sound and Audio Post-Production',
    category: 'Videography & Film Production',
    duration_minutes: 120,
    description: 'Capturing broadcast-standard audio on location, avoiding radio interference, dialogue cleanup, and audio mastering in DaVinci Fairlight.',
    suggested_takeaways: [
      { label: 'Mic Placement', description: 'Lavalier concealment and shotgun microphone positioning techniques.' },
      { label: 'Noise Reduction', description: 'Removing reverb, hiss, and HVAC hum without stripping dialogue warmth.' },
    ],
  },
  {
    id: 'video-04',
    title: 'Color Grading & Finishing in DaVinci Resolve',
    category: 'Videography & Film Production',
    duration_minutes: 120,
    description: 'Color management pipelines (ACES & DaVinci YRGB), node-based grading, shot matching, skin tone preservation, and delivery color spaces.',
    suggested_takeaways: [
      { label: 'Node Architecture', description: 'Structuring exposure, balance, look, and film emulation in serial and parallel nodes.' },
      { label: 'Shot Matching', description: 'Using waveform monitors and scopes to achieve seamless scene continuity.' },
    ],
  },
  {
    id: 'video-05',
    title: 'Documentary Filmmaking: From Interview to Final Cut',
    category: 'Videography & Film Production',
    duration_minutes: 120,
    description: 'Conducting compelling on-camera interviews, shooting cinematic B-roll, and structuring emotional narrative arcs in editing.',
    suggested_takeaways: [
      { label: 'Interview Techniques', description: 'Asking evocative questions that elicit authentic, unscripted responses.' },
      { label: 'B-Roll Rhythm', description: 'Shooting sequences that give editors seamless visual transitions.' },
    ],
  },
  {
    id: 'video-06',
    title: 'Drone Cinematography & Aerial Storytelling',
    category: 'Videography & Film Production',
    duration_minutes: 120,
    description: 'Flight kinematics, cinematic gimbal moves (orbit, reveal, parallax), ND filter choice for correct 180° shutter angle, and airspace safety.',
    suggested_takeaways: [
      { label: 'Camera Flight Moves', description: 'Combining yaw, roll, and tilt for multi-axis cinematic reveals.' },
      { label: 'Shutter Rule', description: 'Using ND filters to maintain natural motion blur at 24fps.' },
    ],
  },
  {
    id: 'video-07',
    title: 'Music Video Production on a Budget',
    category: 'Videography & Film Production',
    duration_minutes: 120,
    description: 'Concept treatment creation, playback synchronization on set, creative visual transitions, and high-energy music video editing rhythms.',
    suggested_takeaways: [
      { label: 'Audio Playback', description: 'Speed-ramped playback on set for smooth slow-motion lip sync.' },
      { label: 'Treatment Pitching', description: 'Visual deck preparation that gets artists and record labels excited.' },
    ],
  },
  {
    id: 'video-08',
    title: 'Visual Effects & Green Screen Mastery in After Effects',
    category: 'Videography & Film Production',
    duration_minutes: 120,
    description: 'Keylight chroma keying, spill suppression, camera planar tracking in Mocha, clean plate generation, and realistic composite lighting.',
    suggested_takeaways: [
      { label: 'Green Screen Lighting', description: 'Even backdrop luminance and separating talent from green bounce spill.' },
      { label: 'Planar Tracking', description: 'Tracking complex perspective surfaces for screen replacements.' },
    ],
  },
  {
    id: 'video-09',
    title: '3-Point Lighting for Dramatic Video Interviews',
    category: 'Videography & Film Production',
    duration_minutes: 120,
    description: 'Key, fill, hair/rim, and practical background lighting ratios. Creating high-contrast cinematic corporate and editorial interviews.',
    suggested_takeaways: [
      { label: 'Contrast Ratios', description: 'Choosing between 2:1 corporate look and 8:1 dramatic moody lighting.' },
      { label: 'Negative Fill', description: 'Using solid black floppies to deepen jawline and cheekbone shadows.' },
    ],
  },
  {
    id: 'video-10',
    title: 'Video Editing Pacing & Montage Theory in Premiere Pro',
    category: 'Videography & Film Production',
    duration_minutes: 120,
    description: 'Cutting on action, J-cuts and L-cuts, micro-rhythm pacing, sound design soundscape layering, and keeping viewer attention retention high.',
    suggested_takeaways: [
      { label: 'Pacing Principles', description: 'When to let shots breathe versus rapid rhythmic cutting.' },
      { label: 'Audio J-Cuts', description: 'Leading visual scene changes with audio ambiance transitions.' },
    ],
  },
  {
    id: 'video-11',
    title: 'Livestream Production & Multi-Camera Switching',
    category: 'Videography & Film Production',
    duration_minutes: 120,
    description: 'ATEM hardware switchers, OBS Studio setups, NDI video networking, bitrate optimization, and failure-free broadcast streaming workflows.',
    suggested_takeaways: [
      { label: 'Multi-Cam Setup', description: 'Calibrating color match and sync delay across 4 distinct camera feeds.' },
      { label: 'Redundant Stream Output', description: 'Setting up cellular bonding and secondary fallback stream keys.' },
    ],
  },

  // ── 3. Graphic Design & Visual Communication ──────────────────────────────
  {
    id: 'design-01',
    title: 'Storytelling Through Design',
    category: 'Graphic Design & Visual Communication',
    duration_minutes: 120,
    description: 'How to combine typography, hierarchy, intentional whitespace, and semiotics to craft visual narratives that instantly connect with audiences.',
    suggested_takeaways: [
      { label: 'Visual Hierarchy', description: 'Guiding viewer eye movement with scale, contrast, and layout architecture.' },
      { label: 'Semiotics & Symbols', description: 'Using cultural cues and symbolic weight to convey deep brand meaning.' },
    ],
  },
  {
    id: 'design-02',
    title: 'Color Theory Beyond the Basics',
    category: 'Graphic Design & Visual Communication',
    duration_minutes: 120,
    description: 'Advanced color harmony, psychological color triggers, contrast ratios for accessibility, and palette creation for complex brand systems.',
    suggested_takeaways: [
      { label: 'Color Psychology', description: 'Triggering specific emotions and associations through nuanced hue selection.' },
      { label: 'Accessibility Standards', description: 'Ensuring WCAG compliant contrast in digital and print mediums.' },
    ],
  },
  {
    id: 'design-03',
    title: 'Typography Mastery: Choosing & Pairing Fonts',
    category: 'Graphic Design & Visual Communication',
    duration_minutes: 120,
    description: 'Understanding typeface classifications, pairing serif and sans-serif fonts, kerning, leading, tracking, and editorial layout standards.',
    suggested_takeaways: [
      { label: 'Pairing Rules', description: 'Balancing contrast and harmony between header display faces and body copy.' },
      { label: 'Micro-Typography', description: 'Adjusting optical kerning and line-height for effortless readability.' },
    ],
  },
  {
    id: 'design-04',
    title: 'Creating Impactful Social Media Ad Creatives',
    category: 'Graphic Design & Visual Communication',
    duration_minutes: 120,
    description: 'Designing high-converting ad graphics that stop thumbs, convey value in 3 seconds, and comply with ad platform safety guidelines.',
    suggested_takeaways: [
      { label: 'Visual Hooks', description: 'Crafting the first visual focal point to halt scrolling.' },
      { label: 'Call to Action Design', description: 'Clear visual signposts that drive user engagement.' },
    ],
  },
  {
    id: 'design-05',
    title: 'Print Production, Prepress & Packaging Design',
    category: 'Graphic Design & Visual Communication',
    duration_minutes: 120,
    description: 'Bleed, trim, CMYK color separation, spot colors (Pantone), foil stamping, dielines, and error-free file preparation for print houses.',
    suggested_takeaways: [
      { label: 'Prepress Checklists', description: 'Eliminating RGB conversion surprises and low-resolution artifact traps.' },
      { label: 'Special Finishes', description: 'Setting up varnish, emboss, and die-cut layers correctly.' },
    ],
  },
  {
    id: 'design-06',
    title: 'Vector Illustration & Character Design in Illustrator',
    category: 'Graphic Design & Visual Communication',
    duration_minutes: 120,
    description: 'Pen tool mastery, geometric construction, dynamic character silhouettes, shading palettes, and scalable icon set design.',
    suggested_takeaways: [
      { label: 'Pen Tool Precision', description: 'Placing anchor points only at extrema for ultra-clean bezier curves.' },
      { label: 'Silhouette Readability', description: 'Ensuring character silhouettes are instantly identifiable in thumbnail size.' },
    ],
  },
  {
    id: 'design-07',
    title: 'Logo Design: From Sketch to Scalable Vector System',
    category: 'Graphic Design & Visual Communication',
    duration_minutes: 120,
    description: 'Concept sketching, optical balance vs. geometric grid alignment, responsive logo lockups (horizontal, stacked, icon), and master vector exports.',
    suggested_takeaways: [
      { label: 'Responsive Lockups', description: 'Designing logos that scale gracefully from billboard to 16px favicon.' },
      { label: 'Grid Construction', description: 'Using circular arcs and golden ratio proportions with purpose.' },
    ],
  },
  {
    id: 'design-08',
    title: 'Editorial Book & Magazine Layout Design in InDesign',
    category: 'Graphic Design & Visual Communication',
    duration_minutes: 120,
    description: 'Multi-column grid systems, baseline grid alignment, master pages, paragraph & character styles, and typographic rhythm in publications.',
    suggested_takeaways: [
      { label: 'Baseline Grid Locking', description: 'Keeping lines of text perfectly aligned across adjacent columns.' },
      { label: 'Style Automation', description: 'Structuring GREP styles for instant formatting of lead-ins and headers.' },
    ],
  },
  {
    id: 'design-09',
    title: 'Packaging Design & 3D Mockups for Retail Brands',
    category: 'Graphic Design & Visual Communication',
    duration_minutes: 120,
    description: 'Dieline preparation, mandatory nutritional label compliance, barcode placement, carton structural integrity, and 3D mockups in Adobe Dimension.',
    suggested_takeaways: [
      { label: 'Dieline Geometry', description: 'Accounting for paper thickness and glue tabs in packaging dielines.' },
      { label: 'Shelf Impact', description: 'Designing front panels that stand out against retail competitor aisles.' },
    ],
  },
  {
    id: 'design-10',
    title: 'Poster Design & Visual Impact Theory',
    category: 'Graphic Design & Visual Communication',
    duration_minutes: 120,
    description: 'Experimental layout, Swiss design principles, brutalist typography, dynamic diagonal tension, and large-format visual communication.',
    suggested_takeaways: [
      { label: 'Visual Hierarchy Anchor', description: 'Creating one dominant focal point with clear secondary support.' },
      { label: 'Negative Space Tension', description: 'Using deliberate emptiness to amplify message weight.' },
    ],
  },
  {
    id: 'design-11',
    title: 'Infographic & Data Visualization Design',
    category: 'Graphic Design & Visual Communication',
    duration_minutes: 120,
    description: 'Transforming dense datasets, statistics, and corporate annual reports into clear, visually engaging diagrams and visual charts.',
    suggested_takeaways: [
      { label: 'Data Simplification', description: 'Choosing the right chart type to reveal trends rather than clutter.' },
      { label: 'Visual Metaphors', description: 'Using relatable illustrations to anchor complex statistical findings.' },
    ],
  },

  // ── 4. Brand Strategy & Creative Direction ─────────────────────────────────
  {
    id: 'brand-01',
    title: 'Designing Brands That Sell',
    category: 'Brand Strategy & Creative Direction',
    duration_minutes: 120,
    description: 'Moving from generic logo design to building robust, commercial identity systems that drive premium pricing and market differentiation.',
    suggested_takeaways: [
      { label: 'Brand Archetypes', description: 'Aligning visual personality with consumer emotional drivers.' },
      { label: 'Brand Guidelines', description: 'Documenting clear usage rules for typography, color, photography, and tone of voice.' },
    ],
  },
  {
    id: 'brand-02',
    title: 'Creative Direction: Leading Projects From Brief to Delivery',
    category: 'Brand Strategy & Creative Direction',
    duration_minutes: 120,
    description: 'The creative director toolkit: interpreting ambiguous client briefs, facilitating team brainstorms, crafting moodboards, and protecting creative integrity.',
    suggested_takeaways: [
      { label: 'Moodboarding Mastery', description: 'Translating strategic concepts into concrete visual references.' },
      { label: 'Client Feedback Management', description: 'Guiding stakeholders away from subjective opinions toward strategic objectives.' },
    ],
  },
  {
    id: 'brand-03',
    title: 'Building Modern UI/UX Design Systems',
    category: 'Brand Strategy & Creative Direction',
    duration_minutes: 120,
    description: 'Scalable design systems in Figma: design tokens, atomic components, interactive auto-layouts, and handoff workflows for developers.',
    suggested_takeaways: [
      { label: 'Component Architecture', description: 'Building atomic UI components that scale effortlessly across web and mobile.' },
      { label: 'Developer Handoff', description: 'Annotating responsive layouts and token specs for seamless implementation.' },
    ],
  },
  {
    id: 'brand-04',
    title: 'Brand Positioning in Competitive Markets',
    category: 'Brand Strategy & Creative Direction',
    duration_minutes: 120,
    description: 'Identifying white space in crowded industries, formulating unique value propositions, and repositioning legacy brands for contemporary audiences.',
    suggested_takeaways: [
      { label: 'Competitor Analysis', description: 'Mapping market landscape to find uncontested positioning territories.' },
      { label: 'Value Proposition', description: 'Condensing complex offerings into single-sentence clarity.' },
    ],
  },
  {
    id: 'brand-05',
    title: 'Rebranding Legacy Companies for Modern Audiences',
    category: 'Brand Strategy & Creative Direction',
    duration_minutes: 120,
    description: 'Managing brand equity preservation, stakeholder interviews, internal rollout communication, and public rebrand launch campaigns.',
    suggested_takeaways: [
      { label: 'Equity Audit', description: 'Identifying core visual elements that customers recognize before redesigning.' },
      { label: 'Change Management', description: 'Aligning internal staff with new brand values before external launches.' },
    ],
  },
  {
    id: 'brand-06',
    title: 'Creative Strategy & Writing Winning Pitches',
    category: 'Brand Strategy & Creative Direction',
    duration_minutes: 120,
    description: 'Structuring agency pitch presentations, storytelling frameworks, identifying client pain points, and presenting creative concepts convincingly.',
    suggested_takeaways: [
      { label: 'Pitch Narrative Arc', description: 'Leading with the problem insight before unveiling creative solutions.' },
      { label: 'De-Risking Ideas', description: 'Showing how creative executions directly impact business revenue.' },
    ],
  },
  {
    id: 'brand-07',
    title: 'User Research & Empathy Mapping for Product Designers',
    category: 'Brand Strategy & Creative Direction',
    duration_minutes: 120,
    description: 'Qualitative user interviews, usability testing sessions, persona generation, and empathy mapping that ground UI decisions in data.',
    suggested_takeaways: [
      { label: 'Interview Synthesis', description: 'Extracting behavioral pain points from user feedback recordings.' },
      { label: 'Usability Benchmarks', description: 'Measuring task completion rates and friction points before launch.' },
    ],
  },
  {
    id: 'brand-08',
    title: 'Web Design Best Practices: Conversion & Storytelling',
    category: 'Brand Strategy & Creative Direction',
    duration_minutes: 120,
    description: 'Hero section architecture, visual scroll hooks, responsive breakpoints, social proof placement, and high-converting landing pages.',
    suggested_takeaways: [
      { label: 'Hero Conversion', description: 'Optimizing the above-the-fold value proposition and primary action button.' },
      { label: 'Visual Hierarchy Flow', description: 'Directing user eye path smoothly down modern long-scroll pages.' },
    ],
  },
  {
    id: 'brand-09',
    title: 'Motion Design for Brand Identities (Lottie & After Effects)',
    category: 'Brand Strategy & Creative Direction',
    duration_minutes: 120,
    description: 'Giving brands motion DNA: logo animation idents, UI micro-interactions, easing curve physics, and lightweight Lottie JSON exports for web.',
    suggested_takeaways: [
      { label: 'Motion Guidelines', description: 'Defining brand velocity curves, bounce vs. sharp snap transitions.' },
      { label: 'Lottie Optimization', description: 'Exporting lightweight vector animations that load in milliseconds on mobile.' },
    ],
  },
  {
    id: 'brand-10',
    title: 'Design Sprint Methodology for Rapid Product Prototyping',
    category: 'Brand Strategy & Creative Direction',
    duration_minutes: 120,
    description: 'Running 5-day Google Ventures style design sprints: problem framing, ideation, storyboard sketching, prototyping, and live user validation.',
    suggested_takeaways: [
      { label: 'Sprint Facilitation', description: 'Guiding cross-functional teams to make swift product decisions without endless debate.' },
      { label: 'Rapid Prototyping', description: 'Building high-fidelity interactive prototypes in Figma in less than 24 hours.' },
    ],
  },

  // ── 5. AI Tools & Future Creative Skills ───────────────────────────────────
  {
    id: 'ai-01',
    title: 'Using AI Without Losing Your Creativity',
    category: 'AI Tools & Future Creative Skills',
    duration_minutes: 120,
    description: 'Integrating generative AI tools into creative ideation and asset generation while preserving individual human artistic style and originality.',
    suggested_takeaways: [
      { label: 'Prompt Engineering', description: 'Crafting precise visual prompts using lighting, camera angles, and art styles.' },
      { label: 'Creative Synergy', description: 'Using AI as a rapid storyboard generator rather than a final asset replacement.' },
    ],
  },
  {
    id: 'ai-02',
    title: 'AI Tools Every Creative Professional Should Know',
    category: 'AI Tools & Future Creative Skills',
    duration_minutes: 120,
    description: 'A hands-on breakdown of state-of-the-art AI applications for image generation, automated video editing, speech cleanup, and workflow automation.',
    suggested_takeaways: [
      { label: 'Workflow Acceleration', description: 'Automating tedious masking, rotoscoping, and background generation tasks.' },
      { label: 'Tool Evaluation', description: 'Selecting the right AI tool stack for design, audio, copy, and video.' },
    ],
  },
  {
    id: 'ai-03',
    title: 'The Future of AI and Creative Careers',
    category: 'AI Tools & Future Creative Skills',
    duration_minutes: 120,
    description: 'Understanding macro industry shifts, high-value skills that cannot be automated, and how creative professionals can thrive in the next decade.',
    suggested_takeaways: [
      { label: 'Future-Proofing', description: 'Cultivating taste, critical thinking, strategic advisory, and client relationships.' },
      { label: 'Hybrid Skillsets', description: 'Combining creative intuition with technical orchestration.' },
    ],
  },
  {
    id: 'ai-04',
    title: 'AI Image Generation with Midjourney & Stable Diffusion',
    category: 'AI Tools & Future Creative Skills',
    duration_minutes: 120,
    description: 'Parameter tuning (`--ar`, `--s`, `--c`), image weighting, ControlNet pose conditioning, LoRA model training, and photorealistic generation.',
    suggested_takeaways: [
      { label: 'ControlNet Mastery', description: 'Forcing AI models to match exact composition layouts and human poses.' },
      { label: 'Consistency Workflows', description: 'Generating consistent characters across varied scenes and lighting conditions.' },
    ],
  },
  {
    id: 'ai-05',
    title: 'AI Audio Cleanup, Voice Cloning & Podcast Production',
    category: 'AI Tools & Future Creative Skills',
    duration_minutes: 120,
    description: 'Adobe Podcast Enhance, ElevenLabs voice cloning, automated transcript generation with Whisper, and removing background echoes with AI.',
    suggested_takeaways: [
      { label: 'Voice Modulation', description: 'Cloning and editing voiceovers for multitrack podcast localization.' },
      { label: 'Spectral Cleanup', description: 'Restoring damaged phone and field recordings into studio-grade audio.' },
    ],
  },
  {
    id: 'ai-06',
    title: 'AI Video Generation & Neural Rendering Techniques',
    category: 'AI Tools & Future Creative Skills',
    duration_minutes: 120,
    description: 'Runway Gen-3, Luma Dream Machine, Kling AI, video-to-video style transfers, and integrating AI video clips into commercial edits.',
    suggested_takeaways: [
      { label: 'Motion Guidance', description: 'Controlling camera movement and subject velocity with motion brushes.' },
      { label: 'Hybrid VFX Pipeline', description: 'Compositing AI-generated video plates into live-action footage.' },
    ],
  },
  {
    id: 'ai-07',
    title: 'Building Custom AI Creative Assistants for Designers',
    category: 'AI Tools & Future Creative Skills',
    duration_minutes: 120,
    description: 'Creating custom GPTs, fine-tuning system prompts with brand guidelines, automating client brief analysis, and generating tailored copy.',
    suggested_takeaways: [
      { label: 'Custom Knowledge Bases', description: 'Feeding brand books and tone-of-voice docs to specialized AI agents.' },
      { label: 'Copy Ideation', description: 'Generating dozens of conversion headline variations in seconds.' },
    ],
  },
  {
    id: 'ai-08',
    title: 'Ethical AI Integration in Commercial Creative Studios',
    category: 'AI Tools & Future Creative Skills',
    duration_minutes: 120,
    description: 'Navigating copyright ownership, commercial liability, client transparency clauses, and fair compensation for original human artists.',
    suggested_takeaways: [
      { label: 'Commercial Terms', description: 'Understanding license rights and indemnity when using AI output in client campaigns.' },
      { label: 'Studio Disclosures', description: 'Writing clear client contract clauses about AI usage in creative workflows.' },
    ],
  },
  {
    id: 'ai-09',
    title: 'Synthetic Media & Future of Visual Content Creation',
    category: 'AI Tools & Future Creative Skills',
    duration_minutes: 120,
    description: 'NeRFs (Neural Radiance Fields), Gaussian Splatting, 3D world building with AI, and the evolution of virtual cinematography.',
    suggested_takeaways: [
      { label: 'Gaussian Splatting', description: 'Converting phone video sweeps into navigable photorealistic 3D camera sets.' },
      { label: 'Virtual Production', description: 'Replacing physical locations with real-time neural virtual environments.' },
    ],
  },

  // ── 6. Creative Business, Freelancing & Agency Growth ──────────────────────
  {
    id: 'biz-01',
    title: 'Pricing Your Work: From Hourly Rates to Value-Based Billing',
    category: 'Creative Business, Freelancing & Agency Growth',
    duration_minutes: 120,
    description: 'Break free from trading time for money. Learn value-based pricing, calculating commercial ROI for clients, and quoting high-ticket creative projects.',
    suggested_takeaways: [
      { label: 'Value-Based Pricing', description: 'Pricing based on commercial upside and client business scale.' },
      { label: 'Proposal Frameworks', description: 'Presenting tiered 3-option proposals that anchor high budgets.' },
    ],
  },
  {
    id: 'biz-02',
    title: 'Contracts & Intellectual Property Every Creative Must Know',
    category: 'Creative Business, Freelancing & Agency Growth',
    duration_minutes: 120,
    description: 'Demystifying copyright ownership, usage licensing terms, kill fees, scope creep prevention, and payment terms in commercial contracts.',
    suggested_takeaways: [
      { label: 'Usage Licensing', description: 'Licensing rights per geography, duration, and medium rather than surrendering raw files.' },
      { label: 'Scope Protection', description: 'Writing ironclad milestone clauses that prevent unpaid revision loops.' },
    ],
  },
  {
    id: 'biz-03',
    title: 'Finding High-Paying Clients and Building Retainers',
    category: 'Creative Business, Freelancing & Agency Growth',
    duration_minutes: 120,
    description: 'Outbound pitching strategies, closing corporate retainers, client qualification frameworks, and eliminating dry revenue months.',
    suggested_takeaways: [
      { label: 'Retainer Agreements', description: 'Structuring ongoing monthly deliverable packs that guarantee cash flow.' },
      { label: 'Client Outreach', description: 'Personalized video pitches and value-first audits that win boardroom attention.' },
    ],
  },
  {
    id: 'biz-04',
    title: 'Growing From Freelancer to Creative Agency Owner',
    category: 'Creative Business, Freelancing & Agency Growth',
    duration_minutes: 120,
    description: 'Hiring freelancers and full-time talent, standard operating procedures (SOPs), agency project management, and scalable studio operations.',
    suggested_takeaways: [
      { label: 'Delegation & SOPs', description: 'Documenting production workflows so projects run smoothly without micromanagement.' },
      { label: 'Financial Management', description: 'Managing agency profit margins, tax obligations, and equipment reinvestment.' },
    ],
  },
  {
    id: 'biz-05',
    title: 'Negotiation Skills for Creative Professionals',
    category: 'Creative Business, Freelancing & Agency Growth',
    duration_minutes: 120,
    description: 'Confidently negotiating creative fees, handling pushback on budgets, defending deliverables, and standing firm without losing the client.',
    suggested_takeaways: [
      { label: 'Anchor Negotiation', description: 'Leading negotiations with high anchor points and non-monetary trade-offs.' },
      { label: 'Scope Reductions', description: 'How to reduce scope proportionally when clients request budget discounts.' },
    ],
  },
  {
    id: 'biz-06',
    title: 'Client Onboarding, Scoping & Discovery Workshops',
    category: 'Creative Business, Freelancing & Agency Growth',
    duration_minutes: 120,
    description: 'Leading paid client discovery sessions, establishing KPIs, setting expectations, and preventing misunderstandings before design commences.',
    suggested_takeaways: [
      { label: 'Paid Discovery', description: 'Charging for strategic scoping before committing to production quotes.' },
      { label: 'Expectation Setting', description: 'Setting communication boundaries and revision limits from day one.' },
    ],
  },
  {
    id: 'biz-07',
    title: 'Building an International Freelance Client Base',
    category: 'Creative Business, Freelancing & Agency Growth',
    duration_minutes: 120,
    description: 'Landing remote creative contracts with US, UK, and European agencies, handling global payments (Wise/Payoneer), and managing timezone collaboration.',
    suggested_takeaways: [
      { label: 'Global Positioning', description: 'Positioning your skills to compete confidently on international marketplaces.' },
      { label: 'Cross-Border Invoicing', description: 'Handling currency conversions, tax forms (W-8BEN), and multi-currency accounts.' },
    ],
  },
  {
    id: 'biz-08',
    title: 'Financial Planning, Taxes & Invoicing for Creatives',
    category: 'Creative Business, Freelancing & Agency Growth',
    duration_minutes: 120,
    description: 'Bookkeeping basics, separating personal and business finances, tracking deductible expenses, emergency runway funds, and cash flow forecasting.',
    suggested_takeaways: [
      { label: 'Runway Planning', description: 'Maintaining 6 months of operating expenses to weather industry lulls.' },
      { label: 'Expense Tracking', description: 'Writing off software subscriptions, gear depreciations, and studio leases.' },
    ],
  },
  {
    id: 'biz-09',
    title: 'Client Pitch Decks & Proposal Design',
    category: 'Creative Business, Freelancing & Agency Growth',
    duration_minutes: 120,
    description: 'Designing high-impact proposal PDF decks that highlight client problems, outline solutions, showcase social proof, and win high-budget contracts.',
    suggested_takeaways: [
      { label: 'Proposal Structure', description: 'Structuring the 10 essential slides that close agency deals.' },
      { label: 'Pricing Option Anchoring', description: 'Positioning the ideal package between an essential tier and an enterprise tier.' },
    ],
  },
  {
    id: 'biz-10',
    title: 'Studio Workflow Automation with Notion & Zapier',
    category: 'Creative Business, Freelancing & Agency Growth',
    duration_minutes: 120,
    description: 'Automating lead capture, client intake questionnaires, project dashboards, automated feedback reminders, and asset delivery pipelines.',
    suggested_takeaways: [
      { label: 'No-Code Automation', description: 'Connecting forms, CRM leads, and Slack alerts with seamless Zapier triggers.' },
      { label: 'Client Portals', description: 'Building clean Notion dashboards for transparent client progress tracking.' },
    ],
  },

  // ── 7. Career Development & Personal Branding ──────────────────────────────
  {
    id: 'career-01',
    title: 'Welcome to the Creative Industry',
    category: 'Career Development & Personal Branding',
    duration_minutes: 120,
    description: 'An essential orientation for emerging creatives. Understanding industry dynamics, studio etiquette, terminology, and realistic career trajectories.',
    suggested_takeaways: [
      { label: 'Industry Navigation', description: 'Understanding different roles, department responsibilities, and creative career paths.' },
      { label: 'Professional Mindset', description: 'Building the resilience, punctuality, and communication needed for long-term success.' },
    ],
  },
  {
    id: 'career-02',
    title: 'Building a Portfolio That Gets You Hired',
    category: 'Career Development & Personal Branding',
    duration_minutes: 120,
    description: 'Curating case studies, showcasing creative problem-solving, writing engaging project breakdowns, and designing a portfolio that impresses creative directors.',
    suggested_takeaways: [
      { label: 'Case Study Structure', description: 'Presenting the client challenge, strategic process, creative execution, and measurable outcome.' },
      { label: 'Curation Quality', description: 'Selecting 5 exceptional showcase projects over 20 mediocre pieces.' },
    ],
  },
  {
    id: 'career-03',
    title: 'Personal Branding and LinkedIn for Creatives',
    category: 'Career Development & Personal Branding',
    duration_minutes: 120,
    description: 'Optimizing LinkedIn profiles, thought leadership posting, building an engaged network, and attracting inbound project opportunities.',
    suggested_takeaways: [
      { label: 'Profile Optimization', description: 'Writing a compelling bio and headline that highlights specific creative value.' },
      { label: 'Content Strategy', description: 'Sharing behind-the-scenes insights and project learnings that build credibility.' },
    ],
  },
  {
    id: 'career-04',
    title: 'Creative Habits That Make You Stand Out',
    category: 'Career Development & Personal Branding',
    duration_minutes: 120,
    description: 'Daily creative routines, active listening skills, proactive client communication, and reliable delivery practices that build a stellar reputation.',
    suggested_takeaways: [
      { label: 'Daily Discipline', description: 'Allocating dedicated time for skill sharpening and creative experimentation.' },
      { label: 'Reliability Factor', description: 'Why meeting deadlines consistently beats raw talent in creative commercial success.' },
    ],
  },
  {
    id: 'career-05',
    title: 'Managing Creative Burnout and Imposter Syndrome',
    category: 'Career Development & Personal Branding',
    duration_minutes: 120,
    description: 'Actionable strategies for sustaining long-term creative stamina, overcoming comparison anxiety, managing stress, and rekindling creative passion.',
    suggested_takeaways: [
      { label: 'Boundary Setting', description: 'Establishing clear work hours and digital disconnect rituals.' },
      { label: 'Perspective Shifts', description: 'Recognizing imposter feelings as signs of growth and reframing self-doubt.' },
    ],
  },
  {
    id: 'career-06',
    title: 'The Creative Job Interview & Live Design Challenges',
    category: 'Career Development & Personal Branding',
    duration_minutes: 120,
    description: 'How to prepare for agency interviews, present your portfolio live to hiring managers, tackle design whiteboard challenges, and negotiate starting salaries.',
    suggested_takeaways: [
      { label: 'Live Presentation', description: 'Walking interviewers through your design thinking and handling constructive critique.' },
      { label: 'Salary Negotiation', description: 'Benchmarking industry market rates and negotiating total compensation packages.' },
    ],
  },
  {
    id: 'career-07',
    title: 'Freelancing vs. Full-Time Studio Employment',
    category: 'Career Development & Personal Branding',
    duration_minutes: 120,
    description: 'Comparing freedom vs. security, studio hierarchy, benefits, networking opportunities, and choosing the right path for your life stage.',
    suggested_takeaways: [
      { label: 'Pros & Cons Analysis', description: 'Evaluating work-life balance, earning ceilings, and career growth speed.' },
      { label: 'Transition Strategies', description: 'Safely transitioning between full-time employment and freelance independence.' },
    ],
  },
  {
    id: 'career-08',
    title: 'Creative Leadership & Mentoring Junior Designers',
    category: 'Career Development & Personal Branding',
    duration_minutes: 120,
    description: 'Developing empathetic leadership skills, giving actionable constructive feedback, conducting design critiques, and elevating team morale.',
    suggested_takeaways: [
      { label: 'Constructive Critique', description: 'Providing feedback focused on project goals rather than subjective taste.' },
      { label: 'Mentorship Culture', description: 'Fostering a supportive studio environment where junior talent thrives.' },
    ],
  },
  {
    id: 'career-09',
    title: 'Public Speaking & Presentation Skills for Creatives',
    category: 'Career Development & Personal Branding',
    duration_minutes: 120,
    description: 'Overcoming stage fright, structuring keynotes, designing beautiful presentation slides, voice modulation, and captivating audience attention.',
    suggested_takeaways: [
      { label: 'Keynote Structure', description: 'Hooking audiences in the first 60 seconds with relatable stories.' },
      { label: 'Slide Design Rules', description: 'Keeping slides visual and minimal with zero bullet-point clutter.' },
    ],
  },
  {
    id: 'career-10',
    title: 'Building a Side Project That Generates Passive Income',
    category: 'Career Development & Personal Branding',
    duration_minutes: 120,
    description: 'Creating and selling digital design assets (Lightroom presets, UI kits, templates, LUTs), digital courses, and diversifying creative revenue.',
    suggested_takeaways: [
      { label: 'Asset Marketplaces', description: 'Publishing and marketing templates on Gumroad, Creative Market, and personal sites.' },
      { label: 'Audience Building', description: 'Converting free tutorials into paid digital product sales funnels.' },
    ],
  },
]
